// Duration utility service for parsing and validating audio durations
export class DurationUtils {
  /**
   * Parse duration string to total seconds
   * Supports MM:SS and H:MM:SS formats
   */
  static parseDurationToSeconds(duration: string): number {
    if (!duration) return 0;
    
    try {
      const durationParts = duration.split(':');
      let totalSeconds = 0;

      if (durationParts.length === 2) {
        // MM:SS format (e.g., "20:05" or "120:30")
        const minutes = parseInt(durationParts[0], 10);
        const seconds = parseInt(durationParts[1], 10);
        totalSeconds = minutes * 60 + seconds;
      } else if (durationParts.length === 3) {
        // H:MM:SS format (e.g., "1:20:05")  
        const hours = parseInt(durationParts[0], 10);
        const minutes = parseInt(durationParts[1], 10);
        const seconds = parseInt(durationParts[2], 10);
        totalSeconds = hours * 3600 + minutes * 60 + seconds;
      } else {
        console.log('⚠️ Invalid duration format:', duration);
        return 0;
      }
      
      return !isNaN(totalSeconds) ? totalSeconds : 0;
    } catch (error) {
      console.warn('⚠️ Error parsing duration:', duration, error);
      return 0; // Safe fallback
    }
  }

  /**
   * Check if project needs auto-split functionality (>10 minutes)
   */
  static checkIfNeedsAutoSplit(projectData: any): boolean {
    try {
      if (!projectData?.duration) return false;
      const totalSeconds = DurationUtils.parseDurationToSeconds(projectData.duration);
      console.log(`🕐 Project duration: ${projectData.duration} (${totalSeconds}s)`);
      return totalSeconds > 600; // 10 minutes = 600 seconds
    } catch (error) {
      console.warn('⚠️ Error checking auto-split need:', error);
      return false; // Safe fallback - keeps original AudioEditor
    }
  }

  /**
   * Format seconds to readable duration string
   */
  static formatDuration(seconds: number): string {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    
    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  }

  /**
   * Check if duration is considered "long" (>20 minutes) for auto-splitting
   */
  static isLongDuration(duration: string): boolean {
    const totalSeconds = DurationUtils.parseDurationToSeconds(duration);
    return totalSeconds > 1200; // 20 minutes = 1200 seconds
  }

  /**
   * Get human-readable duration description
   */
  static getDurationDescription(duration: string): string {
    const totalSeconds = DurationUtils.parseDurationToSeconds(duration);
    
    if (totalSeconds === 0) return 'Unknown duration';
    if (totalSeconds < 60) return `${totalSeconds} seconds`;
    if (totalSeconds < 600) return `${Math.round(totalSeconds / 60)} minutes`;
    if (totalSeconds < 3600) return `${Math.round(totalSeconds / 60)} minutes (uses split editor)`;
    
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    return `${hours}h ${minutes}m (uses split editor with auto-splitting)`;
  }
}
